import { useTheme } from "@emotion/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteUser, fetchUsers } from "reduxStore/actions/userActions";
import { fetchAllLocations } from "reduxStore/actions/locationActions";
import { fetchDivisions } from "reduxStore/actions/divisionActions";
import { fetchLevels } from "reduxStore/actions/levelActions";
import DataGridCustomToolbar from "components/DataGridCustomToolBar";
import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { EditOutlined } from "@mui/icons-material";
import { DeleteOutline } from "@mui/icons-material";
import DeleteUserConfirmationDialog from "./deleteUserConfirmationDialog";
import { setSnackbar } from "reduxStore/state";
import { checkRoles, getPermissionFlags, getUserScope } from "utils/permissions";
import { buildLookupById, getMultiNames } from "utils/entityLookup";

function UserGrid({ handleEditOpen }) {
  const { palette } = useTheme();
  const user = useSelector((state) => state.user);
  const { locationIds } = getUserScope(user);
  const userLocationId = locationIds[0] || "";

  const { showOrg, showLocation } = getPermissionFlags(user);
  const { isAcademyImprovementLead } = checkRoles(user);
  const backgroundAlt = palette.background.alt;
  const primaryLight = palette.primary.light;
  const primaryDark = palette.primary.dark;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [filterModel, setFilterModel] = useState({ items: [] });

  const dispatch = useDispatch();

  const users = useSelector((state) => state.users.data || []);
  const totalUsers = useSelector((state) => state.users.total || 0);

  const locationsAll = useSelector((state) => state.locationsAll || []);
  const divisionsAll = useSelector((state) => state.divisions?.data || []);
  const levelsAll = useSelector((state) => state.levels?.data || []);

  const locationLookup = useMemo(
    () => buildLookupById(locationsAll),
    [locationsAll],
  );
  const divisionLookup = useMemo(
    () => buildLookupById(divisionsAll),
    [divisionsAll],
  );
  const levelLookup = useMemo(() => buildLookupById(levelsAll), [levelsAll]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [sortModel, setSortModel] = useState([
    { field: "createdAt", sort: "desc" }, // Default sorting by createdAt
  ]);

  const handleDeleteOpen = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setUserToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteUserConfirm = async () => {
    try {
      const response = await dispatch(deleteUser(userToDelete._id));

      // Ensure response is an object
      if (!response || typeof response !== "object") {
        throw new Error("Unexpected response format");
      }

      // Determine whether the response was a success or an error
      const isSuccess = response.success && response.message;
      const message = response.message || "An unexpected error occurred";

      dispatch(
        setSnackbar({
          open: true,
          message,
          severity: isSuccess ? "success" : "error",
        }),
      );

      if (isSuccess) {
        closeDeleteDialog();
      }
    } catch (error) {
      console.error("Error while deleting user:", error);

      const errorMessage =
        error?.message || "Failed to delete user. Please try again.";

      dispatch(
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        }),
      );
    }
  };

  const columns = [
    { field: "firstName", headerName: "First Name", flex: 1, sortable: true },
    { field: "lastName", headerName: "Last Name", flex: 1, sortable: true },
    { field: "email", headerName: "Email", flex: 1, sortable: true },
    {
      field: "status",
      headerName: "Status",
      flex: 0.75,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (params.row.status === "active") {
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              px="5px"
              py="3px"
              borderRadius="5px"
              m="3px"
              mb="5px"
              fontSize={10}
              backgroundColor="green"
              color="white"
            >
              Active
            </Box>
          );
        } else if (params.row.status === "deleted") {
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              px="5px"
              py="3px"
              borderRadius="5px"
              m="3px"
              mb="5px"
              fontSize={10}
              backgroundColor="red"
              color="white"
            >
              Deleted
            </Box>
          );
        } else {
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              px="5px"
              py="3px"
              borderRadius="5px"
              m="3px"
              mb="5px"
              fontSize={10}
              backgroundColor="blue"
              color="white"
            >
              Inactive
            </Box>
          );
        }
      },
    },
    {
      field: "roles",
      headerName: "Roles",
      flex: 0.75,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params.row.roles || params.row.roles.length === 0) {
          return null;
        }

        const getInitials = (name) =>
          name
            .split(" ")
            .map((word) => word[0])
            .join("")
            .toUpperCase();

        // Map through roles, ensuring each has a unique key
        const roleNames = params.row.roles.map((role, index) => (
          <Box
            key={`${role._id}-${index}`} // Ensure uniqueness by combining _id and index
            display="flex"
            alignItems="center"
            px="5px"
            py="3px"
            borderRadius="5px"
            m="3px"
            mb="5px"
            fontSize={10}
            backgroundColor={primaryDark}
            color={primaryLight}
            cursor="pointer"
          >
            {getInitials(role.name)}
          </Box>
        ));

        return (
          <Box display="flex" flexDirection="row" flexWrap="wrap">
            {roleNames}
          </Box>
        );
      },
    },
    {
      field: "division",
      headerName: "Subject",
      flex: 1,
      sortable: true, // Enable sorting
      valueGetter: (params) => {
        return getMultiNames(
          params.row.divisions,
          params.row.division,
          divisionLookup,
        );
      }, // Get value for sorting
    },
    {
      field: "level",
      headerName: "Year",
      flex: 1,
      sortable: true, // Enable sorting
      valueGetter: (params) => {
        return getMultiNames(params.row.levels, params.row.level, levelLookup);
      },
    },
    (showOrg || isAcademyImprovementLead) && {
      field: "location",
      headerName: "School",
      flex: 1,
      sortable: true, // Enable sorting
      valueGetter: (params) => {
        return getMultiNames(
          params.row.locations,
          params.row.location,
          locationLookup,
        );
      },
    },
    showLocation && {
      field: "edit",
      headerName: "Edit",
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <button
          onClick={() => handleEditOpen(params.row)} // Open the edit dialog with unit details
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <EditOutlined sx={{ color: primaryDark }} />
        </button>
      ),
    },
    showLocation && {
      field: "delete",
      headerName: "Delete",
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <button
          onClick={() => handleDeleteOpen(params.row)} // Open the delete dialog with unit details
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <DeleteOutline sx={{ color: "red" }} />
        </button>
      ),
    },
  ].filter(Boolean);

  useEffect(() => {
    // Load lookup lists so id arrays (levels/divisions/locations) can be shown with names.
    dispatch(fetchAllLocations());
    dispatch(
      fetchLevels({ page: 0, limit: 1000, sort: "", search: "", location: "" }),
    );
    dispatch(
      fetchDivisions({
        page: 1,
        pageSize: 1000,
        sort: "createdAt",
        order: "desc",
        search: "",
        location: "",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const filters = {
      page,
      pageSize,
      sort: sortModel.length > 0 ? sortModel[0].field : "createdAt", // Default to createdAt if no sort model
      order: sortModel.length > 0 ? sortModel[0].sort : "desc", // Default to descending order
      search,
      location: !showOrg && !isAcademyImprovementLead ? userLocationId : "",
      filters: filterModel,
    };

    dispatch(fetchUsers(filters));
  }, [
    page,
    pageSize,
    sortModel,
    search,
    filterModel,
    dispatch,
    userLocationId,
    showOrg,
    isAcademyImprovementLead,
  ]);

  // When the user stops typing for 500ms, actually apply the search:
  useEffect(() => {
    const h = setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(h);
  }, [searchInput, setSearch, setPage]);

  if (totalUsers === 0) {
    return <div>No users found</div>;
  } // If no users are found, display a message

  return (
    <Box
      mt="40px"
      sx={{
        "& .MuiDataGrid-root": {},
        "& .MuiDataGrid-cell": {
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflow: "visible",
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: backgroundAlt,
        },
        "& .MuiDataGrid-virtualScroller": {
          backgroundColor: backgroundAlt,
        },
        "& .MuiDataGrid-footerContainer": {
          backgroundColor: primaryLight,
        },
        "& .MuiDataGrid-row": {
          height: "auto", // This ensures the row height adapts to content
        },
      }}
    >
      <DataGrid
        getRowId={(row) => row._id}
        rows={users}
        columns={columns}
        page={page - 1}
        pageSize={pageSize}
        rowCount={totalUsers}
        rowsPerPageOptions={[10, 25, 50, 100]}
        pagination
        paginationMode="server"
        autoHeight
        rowHeight={50}
        sortingMode="server" // Enable server-side sorting
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        onPageChange={(newPage) => setPage(newPage + 1)}
        onPageSizeChange={(newPageSize) => {
          setPage(1);
          setPageSize(newPageSize);
        }}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={(newFilterModel) => {
          setPage(1);
          setFilterModel(newFilterModel);
        }}
        // loading={users.length === 0}
        components={{
          Toolbar: DataGridCustomToolbar,
        }}
        componentsProps={{
          toolbar: {
            search,
            setSearch,
            searchInput,
            setSearchInput,
            sort,
            setSort,
            backgroundAlt,
            primaryLight,
            primaryDark,
          },
        }}
      />

      <DeleteUserConfirmationDialog
        open={deleteDialogOpen}
        handleClose={closeDeleteDialog}
        handleDeleteUserConfirm={handleDeleteUserConfirm}
      />
    </Box>
  );
}

export default UserGrid;
